import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { toast } from 'sonner';

export interface ArticleDraft {
  id: string;
  region: string;
  title: string;
  content: string;
  markdown_content?: string;
  source_articles: any[];
  status: string;
  composite_score: number;
  freshness_score?: number;
  trust_score?: number;
  regionality_score?: number;
  novelty_score?: number;
  event_clusters?: any[];
  generation_metadata?: any;
  created_at: string;
  updated_at: string;
}

export const useArticleDrafts = () => {
  const queryClient = useQueryClient();

  const { data: drafts, isLoading, error } = useQuery({
    queryKey: ['article-drafts', 'pending_review'],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('ai_article_drafts')
        .select('*')
        .eq('status', 'pending_review')
        .order('created_at', { ascending: false });

      if (error) throw error;
      return data as ArticleDraft[];
    },
    staleTime: 30000,
  });

  const approveMutation = useMutation({
    mutationFn: async (draftId: string) => {
      const draft = drafts?.find(d => d.id === draftId);
      if (!draft) throw new Error('Draft not found');

      // Move to interviews table
      const { error: insertError } = await supabase
        .from('interviews')
        .insert({
          title: draft.title,
          interviewee_name: 'Kai (AI)',
          company: 'Briefr',
          job_title: 'AI-assistent',
          question: `Hva skjer i ${draft.region}?`,
          answer: draft.content,
          context: `AI-generert oppsummering basert på ${draft.source_articles.length} kilder`,
          tags: [draft.region, 'AI-generert', 'Briefr Bullets'],
          publication_status: 'published',
          article_type: 'ai_generated',
          timestamp: new Date().toISOString(),
        });

      if (insertError) throw insertError;

      // Update draft status
      const { error: updateError } = await supabase
        .from('ai_article_drafts')
        .update({ 
          status: 'approved',
          published_at: new Date().toISOString()
        })
        .eq('id', draftId);

      if (updateError) throw updateError;

      return draftId;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['article-drafts'] });
      toast.success('Artikkel godkjent og publisert');
    },
    onError: (error: Error) => {
      toast.error(`Kunne ikke godkjenne artikkel: ${error.message}`);
    },
  });

  const rejectMutation = useMutation({
    mutationFn: async ({ draftId, reason }: { draftId: string; reason: string }) => {
      const { error } = await supabase
        .from('ai_article_drafts')
        .update({ 
          status: 'rejected',
          rejection_reason: reason,
          updated_at: new Date().toISOString()
        })
        .eq('id', draftId);

      if (error) throw error;
      return draftId;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['article-drafts'] });
      toast.success('Artikkel avvist');
    },
    onError: (error: Error) => {
      toast.error(`Kunne ikke avvise artikkel: ${error.message}`);
    },
  });

  return {
    drafts: drafts || [],
    isLoading,
    error,
    approveDraft: (draftId: string) => approveMutation.mutate(draftId),
    rejectDraft: (draftId: string, reason: string) => 
      rejectMutation.mutate({ draftId, reason }),
  };
};
